/*
 * Copyright 2026 Sony Corporation
 */

import type { AssetSource } from '@ips-device-verse/twin-service/schema/scene-doc';
import type { CatalogAsset } from './polyhaven';

/**
 * What a dragged catalogue tile carries.
 *
 * A private type rather than `text/plain`: it is what lets the viewport accept
 * a drag from the library and refuse one from a browser tab or the desktop.
 */
export const ASSET_MIME = 'application/x-device-verse-asset';

export interface DraggedAsset {
  /** A model is placed where it lands; a texture paints whatever it lands on. */
  kind: 'model' | 'texture';
  /** Absent means Poly Haven, which is what every drag was before there was a second source. */
  source?: AssetSource;
  slug: string;
  name: string;
  pinnedHash?: string;
}

export const packAsset = (kind: DraggedAsset['kind'], asset: CatalogAsset): string =>
  JSON.stringify({ kind, slug: asset.slug, name: asset.name, pinnedHash: asset.filesHash } satisfies DraggedAsset);

export const packSketchfab = (uid: string, name: string): string =>
  JSON.stringify({ kind: 'model', source: 'sketchfab', slug: uid, name } satisfies DraggedAsset);

export const unpackAsset = (transfer: DataTransfer): DraggedAsset | undefined => {
  const raw = transfer.getData(ASSET_MIME);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    // Shape-checked here, slug-checked by the reducer that consumes it.
    if (typeof parsed === 'object' && parsed !== null && 'kind' in parsed && 'slug' in parsed && 'name' in parsed) {
      return parsed as DraggedAsset;
    }
  } catch {
    return undefined;
  }
  return undefined;
};
